import React, { useState } from "react";
import { BackButton, Button, Card, Chip, EmptyState, SectionTitle } from "./ui";

/* -------------------------------------------------------------------------- */
/* Requests — received, sent, accepted, rejected                              */
/* -------------------------------------------------------------------------- */

const tabs = [
  { key: "received", label: "Recibidas" },
  { key: "sent", label: "Enviadas" },
  { key: "accepted", label: "Aceptadas" },
  { key: "rejected", label: "Rechazadas" },
];

export default function Requests({
  received,
  sent,
  onAccept,
  onReject,
  onOpenChat,
  onOpenDog,
  onBack,
  initialTab = "received",
}) {
  const [tab, setTab] = useState(initialTab);

  const accepted = [
    ...received.filter((r) => r.status === "Aceptada"),
    ...sent.filter((r) => r.status === "Aceptada"),
  ];
  const rejected = [
    ...received.filter((r) => r.status === "Rechazada"),
    ...sent.filter((r) => r.status === "Rechazada"),
  ];

  const list = {
    received: received.filter((r) => r.status === "Recibida"),
    sent,
    accepted,
    rejected,
  }[tab];

  return (
    <div className="mx-auto max-w-5xl px-4 pb-28 pt-4 sm:px-6" data-testid="requests-screen">
      <div className="mb-4">
        <BackButton onClick={onBack} testId="requests-back-btn" />
      </div>

      <SectionTitle eyebrow="Comunidad">Solicitudes</SectionTitle>
      <p className="mt-2 text-sm text-slate-500 md:text-base">
        Gestiona tus movimientos de colita entrantes y salientes.
      </p>

      {/* Tabs */}
      <div className="mt-5 flex gap-2 overflow-x-auto rounded-2xl border border-slate-100 bg-white p-1.5 shadow-sm">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`requests-tab-${t.key}`}
            className={`flex-1 whitespace-nowrap rounded-xl px-4 py-2 text-sm font-bold transition ${
              tab === t.key
                ? "bg-[#0B2D4D] text-white shadow-sm"
                : "text-slate-500 hover:bg-slate-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3" data-testid={`requests-list-${tab}`}>
        {list.length === 0 ? (
          <EmptyState
            emoji="🐾"
            title={
              tab === "received"
                ? "Aún no recibes solicitudes"
                : tab === "sent"
                ? "Aún no has enviado colitas"
                : tab === "accepted"
                ? "Nada aceptado todavía"
                : "Nada rechazado"
            }
            text={
              tab === "sent"
                ? "Explora huellitas y envía tu primera solicitud."
                : "Aquí aparecerán tus movimientos de colita."
            }
          />
        ) : (
          list.map((req) => (
            <RequestCard
              key={req.id}
              req={req}
              direction={tab === "sent" ? "sent" : "received"}
              onAccept={() => onAccept(req.id)}
              onReject={() => onReject(req.id)}
              onOpenChat={() => onOpenChat(req.dog.id)}
              onOpenDog={() => onOpenDog(req.dog)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function RequestCard({ req, direction, onAccept, onReject, onOpenChat, onOpenDog }) {
  const toneByStatus = {
    Recibida: "yellow",
    Pendiente: "yellow",
    Aceptada: "mint",
    Rechazada: "gray",
  };

  return (
    <Card className="p-4" data-testid={`request-card-${req.id}`}>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onOpenDog}
          className="shrink-0"
          aria-label={`Ver perfil de ${req.dog.name}`}
        >
          <img
            src={req.dog.image || "/dog-placeholder.svg"}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
            alt={req.dog.name}
            className="h-20 w-20 rounded-2xl object-cover shadow-sm"
          />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-display text-xl font-black text-[#0B2D4D]">
                {req.dog.name}
              </h3>
              <p className="text-sm text-slate-500">
                {req.dog.breed} · {req.dog.sex} · {req.dog.owner}
              </p>
            </div>
            <Chip tone={toneByStatus[req.status] || "gray"}>{req.status}</Chip>
          </div>
          <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600">
            {req.message}
          </p>
          {req.createdAt && (
            <p className="mt-1 text-xs text-slate-400">{req.createdAt}</p>
          )}
        </div>
      </div>

      {direction === "received" && req.status === "Recibida" && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            onClick={onReject}
            testId={`request-reject-${req.id}`}
          >
            Seguir olfateando
          </Button>
          <Button
            variant="mint"
            onClick={onAccept}
            testId={`request-accept-${req.id}`}
          >
            Mover la colita de vuelta
          </Button>
        </div>
      )}

      {req.status === "Aceptada" && (
        <div className="mt-4">
          <Button
            variant="primary"
            onClick={onOpenChat}
            testId={`request-openchat-${req.id}`}
            className="w-full"
          >
            Abrir chat con {req.dog.owner?.split(" ")[0] || req.dog.name}
          </Button>
        </div>
      )}
    </Card>
  );
}
